import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../components/ui/Button';
import WallItemPreview from './WallItemPreview';
import { useWallTimeline } from './useWallTimeline';

/**
 * Smart Template Engine — Preview System (Phase 2A).
 *
 * Plays a whole playlist in order across the wall grid, in real time: each item
 * for its duration (templates render natively, videos split per cell), a timeline
 * bar with per-item segments + playhead, and transport (play/pause/restart/seek).
 * Uses the shared `useWallTimeline` engine. Read-only — zero sync contact.
 *
 * @param {Array}  items   playlist items (content_type, duration_seconds, …)
 * @param {object} deps    { mediaMap, themes, defaultZones, resolvePhoto }
 */
const GRIDS = [{ label: '1×1', rows: 1, cols: 1 }, { label: '1×2', rows: 1, cols: 2 },
  { label: '2×2', rows: 2, cols: 2 }, { label: '1×3', rows: 1, cols: 3 }];

export default function PlaylistPreviewPlayer({ items = [], mediaMap = {}, themes = [], defaultZones = {}, resolvePhoto, maxWidth = 560, speed = 1, initialGrid, lockGrid = false }) {
  const { t } = useTranslation();
  const [grid, setGrid] = useState(initialGrid || { rows: 2, cols: 2 });
  const [playing, setPlaying] = useState(true);

  const startRef = useRef(Date.now());
  const pausedRef = useRef(0);
  const getElapsed = useCallback(
    () => (playing ? Date.now() - startRef.current : pausedRef.current),
    [playing],
  );

  // `speed` fast-forwards by compressing each item's slot (sales-demo / scrub).
  const durationsMs = useMemo(
    () => items.map((i) => Math.max(1, (Math.max(1, Number(i.duration_seconds) || 10) * 1000) / Math.max(0.1, speed))),
    [items, speed],
  );
  const cumStarts = useMemo(() => {
    const out = []; let acc = 0;
    durationsMs.forEach((d) => { out.push(acc); acc += d; });
    return out;
  }, [durationsMs]);

  const { index, totalMs, elapsedMs } = useWallTimeline({ durationsMs, getElapsed, playing, loop: true });

  const seekElapsed = (e) => { startRef.current = Date.now() - e; pausedRef.current = e; };
  const pause = () => { pausedRef.current = Date.now() - startRef.current; setPlaying(false); };
  const play = () => { startRef.current = Date.now() - pausedRef.current; setPlaying(true); };
  const restart = () => { seekElapsed(0); setPlaying(true); };
  const jump = (i) => seekElapsed(cumStarts[i] || 0);

  if (!items.length) {
    return <div className="text-center text-muted p-4">{t('video_wall.preview.empty_playlist')}</div>;
  }

  const current = items[index];
  const playheadPct = totalMs ? (elapsedMs / totalMs) * 100 : 0;

  return (
    <div className="d-flex flex-column align-items-center gap-3">
      {/* grid selector — hidden when locked to a specific wall's geometry */}
      {!lockGrid && (
        <div className="d-flex flex-wrap gap-2 justify-content-center">
          {GRIDS.map((g) => (
            <Button key={g.label} size="small" label={g.label}
              outlined={!(grid.rows === g.rows && grid.cols === g.cols)}
              onClick={() => setGrid({ rows: g.rows, cols: g.cols })} />
          ))}
        </div>
      )}

      {/* current item across the wall */}
      <WallItemPreview item={current} grid={{ ...grid, orientation: 'landscape' }}
        mediaMap={mediaMap} themes={themes} defaultZones={defaultZones} resolvePhoto={resolvePhoto} maxWidth={maxWidth} />

      {/* timeline bar */}
      <div style={{ width: maxWidth, maxWidth: '100%' }}>
        <div style={{ position: 'relative', display: 'flex', gap: 2, height: 26 }}>
          {items.map((it, i) => (
            <div key={it._key || it.id || i} role="button" onClick={() => jump(i)}
              title={t('video_wall.preview.item_n', { n: i + 1 })}
              style={{ flex: durationsMs[i], borderRadius: 4, cursor: 'pointer',
                background: i === index ? 'var(--pp-btn-honey)' : 'var(--pp-bg-tertiary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: i === index ? '#fff' : 'var(--pp-text-secondary)', fontSize: 11, overflow: 'hidden' }}>
              <i className={`pi ${it.content_type === 'template' ? 'pi-palette' : it.content_type === 'split' ? 'pi-th-large' : 'pi-video'}`} />
            </div>
          ))}
          <div style={{ position: 'absolute', top: -2, bottom: -2, left: `${playheadPct}%`, width: 2,
            background: 'var(--pp-text-primary)' }} />
        </div>
      </div>

      {/* transport */}
      <div className="d-flex align-items-center gap-2">
        <Button rounded text icon="pi pi-step-backward" onClick={() => jump(Math.max(0, index - 1))}
          aria-label={t('video_wall.preview.prev')} />
        <Button rounded icon={playing ? 'pi pi-pause' : 'pi pi-play'} onClick={() => (playing ? pause() : play())}
          aria-label={playing ? t('video_wall.preview.pause') : t('video_wall.preview.play')} />
        <Button rounded text icon="pi pi-step-forward" onClick={() => jump(Math.min(items.length - 1, index + 1))}
          aria-label={t('video_wall.preview.next')} />
        <Button rounded text icon="pi pi-replay" onClick={restart} aria-label={t('video_wall.preview.restart')} />
        <span className="small text-muted ms-2">
          {t('video_wall.preview.item_n', { n: index + 1 })} / {items.length}
        </span>
      </div>
    </div>
  );
}
